import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  cityByCountryAndStartsWith,
  cityByDistrictAndStartsWith,
  cityByStateAndStartsWith,
  cityStartsWith,
  findStateAndDistrictAgainstCity,
} from "../api/cityApi";
import { countryStartsWith } from "../api/countryApi";
import { districtByStateAndStartsWith } from "../api/districtApi";
import { getTextByValueAndKey } from "../api/keyvalue";
import { stateByCountryAndStartsWith } from "../api/stateApi";
import Grid from "./Grid";
import LoadingSpinner from "./LoadingSpinner";
import Button from "./input/Button";
import Dropdown from "./input/Dropdown";
import Input from "./input/Input";
import SearchDropdown from "./input/SearchDropdown";
import ButtonRenderer from "./renderer/ButtonRenderer";
import {
  contactValidation,
  dynamicContactValidation,
  dynamicContactValidationStatus,
} from "./validations/stakeholderCreation";
import { getContactLabels, isNumber } from "../helper";
import { AUTH_TYPES, FORMS, MODULES } from "../const";
import useUserCompany from "../hooks/useUserCompany";
import useFormVisibility from "../hooks/useFormVisibility";
import { useSelector } from "react-redux";
import useFieldVisibility from "../hooks/useFieldVisibility";
import useDynamicValidation from "../hooks/useDynamicValidation";
import { ResizableScrollingTextRenderer } from "./renderer/StyleRenderer";
import DeleteButtonRenderer from "./renderer/DeleteButtonRenderer";

const FORM = FORMS.COMMON.CONTACTS.form;

const ContactComponent = forwardRef(
  (
    {
      opeType,
      dropdown,
      isLoading,
      currentValues,
      editMode,
      useDynamicFields = false,
      keyValue,
      type,
    },
    ref,
  ) => {
    const isFormVisibilityLoading = useSelector(
      (state) => state.formVisibility.loading,
    );

    useFormVisibility({
      form: FORM,
      commonForm: true,
      opeType,
      keyValue,
      type,
      useDynamicFields,
    });

    const { visible: isContactSectionVisible } = useFieldVisibility({
      form: `${FORM}_${opeType}`,
      field: "contact_section",
      useDynamicFields,
    });
    const { visible: isPhoneSectionVisible } = useFieldVisibility({
      form: `${FORM}_${opeType}`,
      field: "phone_section",
      useDynamicFields,
    });
    const { visible: isAddressSectionVisible } = useFieldVisibility({
      form: `${FORM}_${opeType}`,
      field: "address_section",
      useDynamicFields,
    });

    const validationStatus = useDynamicValidation(
      dynamicContactValidationStatus,
      `${FORM}_${opeType}`,
      useDynamicFields,
    );

    const userCompany = useUserCompany();
    const stateRef = useRef();
    const countryRef = useRef();
    const secCountryRef = useRef();
    const secStateRef = useRef();
    const districtRef = useRef();
    const secDistrictRef = useRef();
    const cityRef = useRef();
    const secCityRef = useRef();
    const [contacts, setContacts] = useState([]);
    const [conCount, setConCount] = useState(2);

    const [currentErrors, setCurrentErrors] = useState({});
    const [currentContact, setCurrentContact] = useState({});

    const [secondPhoneField, setSecondPhoneField] = useState(false);
    const [secondAddressField, setSecondAddressField] = useState(false);
    const [isAddLoading, setIsAddLoading] = useState(false);

    const [isCountrySet, setIsCountrySet] = useState(true);
    const [isSecCountrySet, setIsSecCountrySet] = useState(true);
    const [isStateSet, setIsStateSet] = useState(false);
    const [isSecStateSet, setIsSecStateSet] = useState(false);
    const [isDistrictSet, setIsDistrictSet] = useState(false);
    const [isSecDistrictSet, setIsSecDistrictSet] = useState(false);
    const [deletedList, setDeletedList] = useState([]);
    const [buttonClicked, setButtonClicked] = useState(false);
    const [searchFieldReset, setSearchFieldReset] = useState(false);

    const [contactData, setContactData] = useState({
      conOperation: opeType,
      conType: 1,
      conTitle: null,
      conName: "",
      conDesignation: "",
      conDepartment: null,
      conPhone: "",
      conMobile: "",
      conWhatsappNo: "",
      conPhoneSecond: "",
      conEmail: "",
      conAddress: "",
      conAddress2: "",
      conAddress3: "",
      conState: null,
      conStateValue: "",
      conDistrict: null,
      conDistrictValue: "",
      conZip: "",
      conCity: null,
      conCityValue: "",
      conCountry: userCompany ? userCompany.comCountry : null,
      conCountryValue: userCompany ? userCompany.comCountryName : "",
      conSecAddress: "",
      conSecAddress2: "",
      conSecAddress3: "",
      conSecState: null,
      conSecStateValue: "",
      conSecDistrict: null,
      conSecDistrictValue: "",
      conSecZip: "",
      conSecCity: null,
      conSecCityValue: "",
      conSecCountry: userCompany ? userCompany.comCountry : null,
      conSecCountryValue: userCompany ? userCompany.comCountryName : "",
      conLon: null,
      conLat: null,
      conStatus: 2,
      conActive: 1,
      conTypeValue: "",
      conTitleValue: "",
      conNic: "",
    });

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentContacts = [];
          let id = 2;
          for (let currentValue of currentValues) {
            const {
              conCountryValue,
              conStateValue,
              conDistrictValue,
              conCityValue,
              conSecCountryValue,
              conSecStateValue,
              conSecDistrictValue,
              conSecCityValue,
            } = await getContactLabels(currentValue);

            currentContacts.push({
              ...currentValue,
              conCountryValue,
              conStateValue,
              conDistrictValue,
              conCityValue,
              conSecCountryValue,
              conSecStateValue,
              conSecDistrictValue,
              conSecCityValue,
              id,
            });
            ++id;
          }
          setConCount(id);
          setContacts(currentContacts);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setContactData({
          conOperation: opeType,
          conType: 1,
          conTitle: null,
          conName: "",
          conDesignation: "",
          conDepartment: null,
          conPhone: "",
          conMobile: "",
          conWhatsappNo: "",
          conPhoneSecond: "",
          conEmail: "",
          conAddress: "",
          conAddress2: "",
          conAddress3: "",
          conState: null,
          conDistrict: null,
          conZip: "",
          conCity: "",
          conCountry: userCompany ? userCompany.comCountry : null,
          conSecAddress: "",
          conSecAddress2: "",
          conSecAddress3: "",
          conSecState: null,
          conSecDistrict: null,
          conSecZip: "",
          conSecCity: null,
          conSecCountry: userCompany ? userCompany.comCountry : null,
          conLon: "",
          conLat: "",
          conStatus: 2,
          conActive: 1,
          conTypeValue: "",
          conTitleValue: "",
          conNic: "",
        });

        setIsCountrySet(true);
        setIsSecCountrySet(true);
        setIsStateSet(false);
        setIsSecStateSet(false);
        setIsDistrictSet(false);
        setIsSecDistrictSet(false);
        setCurrentErrors({});
        setSearchFieldReset(true);

        setButtonClicked(false);
        setCurrentContact({});
      }
    }, [buttonClicked, opeType]);

    useEffect(() => {
      (async () => {
        if (Object.keys(currentContact).length > 0) {
          setContactData({
            ...currentContact,
            conTypeValue: "",
            conTitleValue: "",
          });

          setIsCountrySet(currentContact.conCountry !== null);
          setIsSecCountrySet(currentContact.conSecCountry !== null);
          setIsStateSet(currentContact.conState !== null);
          setIsSecStateSet(currentContact.conSecState !== null);
          setIsDistrictSet(currentContact.conDistrict !== null);
          setIsSecDistrictSet(currentContact.conSecDistrict !== null);
        }
      })();
    }, [currentContact]);

    const getUpdateState = () => {
      const updateState = {
        modified: [],
        unmodified: [],
        created: [],
        deleted: deletedList,
      };

      contacts.forEach((con) => {
        if (con.conId) {
          if (con.modified) {
            updateState.modified.push(con);
          } else {
            updateState.unmodified.push(con);
          }
        } else {
          updateState.created.push(con);
        }
      });

      return updateState;
    };

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          return getUpdateState();
        } else {
          return contacts;
        }
      },
      resetForm: () => {
        setButtonClicked(true);
        setContacts([]);
      },
      validateForm: () => {
        if (editMode) {
          const contactDetails = getUpdateState();
          const mainContacts = contactDetails.modified.filter(
            (con) => con.conType === 1,
          );
          const newMainContacts = contactDetails.created.filter(
            (con) => con.conType === 1,
          );
          const unModifiedMainContacts = contactDetails.unmodified.filter(
            (con) => con.conType === 1,
          );
          return (
            mainContacts.length +
              newMainContacts.length +
              unModifiedMainContacts.length <=
            0
          );
        } else {
          const mainContacts = contacts.filter((con) => con.conType === 1);
          return mainContacts.length <= 0;
        }
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      let contactErrors = {};

      if (useDynamicFields) {
        contactErrors = dynamicContactValidation(
          {
            ...contactData,
          },
          validationStatus,
        );
      } else {
        contactErrors = contactValidation(contactData);
      }

      if (Object.keys(contactErrors).length === 0) {
        const conTypeValue = await getTextByValueAndKey(
          "contact",
          contactData.conType,
        );
        const conTitleValue = await getTextByValueAndKey(
          "title",
          contactData.conTitle,
        );

        const {
          conCountryValue,
          conStateValue,
          conDistrictValue,
          conCityValue,
          conSecCountryValue,
          conSecStateValue,
          conSecDistrictValue,
          conSecCityValue,
        } = await getContactLabels(contactData);

        if (contactData.conType === 1) {
          const mainContacts = contacts.filter((con) => con.conType === 1);

          if (
            mainContacts.length >= 1 &&
            mainContacts[0].id !== contactData.id
          ) {
            contactErrors = {
              ...contactErrors,
              // mainCountError: "Can't have more than one Main contact",
            };
            toast.error("Can't have more than one Main contact", {
              autoClose: 5000,
              containerId: "Main",
            });
            setCurrentErrors(contactErrors);
            setIsAddLoading(false);
            return;
          }
        }
        if (!contactData.conId || contactData.conId === null) {
          if (!contactData.id || contactData.id === null) {
            setContacts((prevState) => [
              ...prevState,
              {
                ...contactData,
                id: conCount,
                conTypeValue,
                conTitleValue,
                conCountryValue,
                conStateValue,
                conDistrictValue,
                conCityValue,
                conSecCountryValue,
                conSecStateValue,
                conSecDistrictValue,
                conSecCityValue,
              },
            ]);
            setConCount((prevState) => ++prevState);
          } else {
            setContacts((prevState) =>
              prevState.filter((item) => item.id !== contactData.id),
            );
            setContacts((prevState) => [
              ...prevState,
              {
                ...contactData,
                conTypeValue,
                conTitleValue,
                conCountryValue,
                conStateValue,
                conDistrictValue,
                conCityValue,
                conSecCountryValue,
                conSecStateValue,
                conSecDistrictValue,
                conSecCityValue,
              },
            ]);
          }
        } else {
          setContacts((prevState) =>
            prevState.filter((item) => item.conId !== contactData.conId),
          );
          setContacts((prevState) => [
            ...prevState,
            {
              ...contactData,
              conTypeValue,
              conTitleValue,
              conCountryValue,
              conStateValue,
              conDistrictValue,
              conCityValue,
              conSecCountryValue,
              conSecStateValue,
              conSecDistrictValue,
              conSecCityValue,
              modified: true,
            },
          ]);
        }
        toast.success("Contact Details Added !", { autoClose: 3000 });
        setButtonClicked(true);
        setIsAddLoading(false);
      } else {
        toast.error("There was an error adding Contact Details !");
      }

      setCurrentErrors(contactErrors);
      setIsAddLoading(false);
    };

    console.log("contactData", contactData);

    useEffect(() => {
      (async () => {
        if (contactData.conCity !== null) {
          const response = await findStateAndDistrictAgainstCity(
            contactData.conCity,
          );
          if (response) {
            setContactData((prevState) => ({
              ...prevState,
              conCityValue: response.citCity,
              conCountry: response.couId,
              conCountryValue: response.couCountry,
              conState: response.sttId,
              conStateValue: response.sttStateName,
              conDistrict: response.disId,
              conDistrictValue: response.disDistrict,
            }));

            setIsCountrySet(true);
            setIsStateSet(true);
            setIsDistrictSet(true);
          }
        } else if (contactData.conSecCity !== null) {
          const response = await findStateAndDistrictAgainstCity(
            contactData.conSecCity,
          );
          if (response) {
            setContactData((prevState) => ({
              ...prevState,
              conSecCityValue: response.citCity,
              conSecCountry: response.couId,
              conSecCountryValue: response.couCountry,
              conSecState: response.sttId,
              conSecStateValue: response.sttStateName,
              conSecDistrict: response.disId,
              conSecDistrictValue: response.disDistrict,
            }));

            setIsSecCountrySet(true);
            setIsSecStateSet(true);
            setIsSecDistrictSet(true);
          }
        }
      })();
    }, [contactData.conCity, contactData.conSecCity]);

    return (
      <>
        {isLoading || isFormVisibilityLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <form
              className="ui form"
              style={{ overflowY: "auto", height: "75vh" }}
            >
              {isContactSectionVisible && (
                <div className="ui segment">
                  <h4 className="ui dividing header">Contact Form</h4>
                  <Dropdown
                    required={true}
                    label="Type"
                    useDynamicFields={true}
                    error={currentErrors.conType}
                    module={MODULES.Master}
                    form={`${FORM}_${opeType}`}
                    field="con_type"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { conType: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Type"
                    options={dropdown.typeOptions}
                    value={contactData.conType}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setContactData((prevState) => ({
                        ...prevState,
                        conType: data.value,
                      }));
                    }}
                  />
                  <div className="two fields">
                    <Dropdown
                      useDynamicFields={true}
                      required={true}
                      label="Title"
                      error={currentErrors.conTitle}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_title"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conTitle: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      placeholder="Title"
                      options={dropdown.titleOptions}
                      value={contactData.conTitle}
                      style={{ height: "5px" }}
                      onChange={(e, data) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conTitle: data.value,
                        }));
                      }}
                    />
                    <Input
                      useDynamicFields={true}
                      required={true}
                      label="Name"
                      error={currentErrors.conName}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_name"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conName: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="conName"
                      value={contactData.conName}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conName: e.target.value,
                        }));
                      }}
                    />
                  </div>
                  <Dropdown
                    useDynamicFields={true}
                    required={true}
                    label="Department"
                    error={currentErrors.conDepartment}
                    module={MODULES.Master}
                    form={`${FORM}_${opeType}`}
                    field="con_department"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { conDepartment: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Department"
                    options={dropdown.departmentOptions}
                    value={contactData.conDepartment}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setContactData((prevState) => ({
                        ...prevState,
                        conDepartment: data.value,
                      }));
                    }}
                  />
                  <div className="two fields">
                    <Input
                      useDynamicFields={true}
                      type="email"
                      name="conEmail"
                      value={contactData.conEmail}
                      placeholder="Email"
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_email"
                      editMode={editMode}
                      required={true}
                      label="Email"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conEmail: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      error={currentErrors.conEmail}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conEmail: e.target.value,
                        }));
                      }}
                    />
                    <Input
                      useDynamicFields={true}
                      label="NIC"
                      error={currentErrors.conNic}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_nic"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conNic: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="conNIC"
                      value={contactData.conNic}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conNic: e.target.value,
                        }));
                      }}
                      setErrors={setCurrentErrors}
                      errorField="conNIC"
                    />
                  </div>
                  <div className="two fields">
                    <Input
                      useDynamicFields={true}
                      label="Longitude"
                      error={currentErrors.conLon}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_lon"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conLon: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="conLon"
                      value={contactData.conLon}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conLon: e.target.value,
                        }));
                      }}
                    />
                    <Input
                      useDynamicFields={true}
                      label="Latitude"
                      error={currentErrors.conLat}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_lat"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conLat: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="conLat"
                      value={contactData.conLat}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conLat: e.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="two fields">
                    <Dropdown
                      useDynamicFields={true}
                      required={true}
                      label="Status"
                      error={currentErrors.conStatus}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_status"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conStatus: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      placeholder="Status"
                      options={dropdown.statusOptions}
                      value={contactData.conStatus}
                      style={{ height: "5px" }}
                      onChange={(e, data) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conStatus: data.value,
                        }));
                      }}
                    />
                    {/* <Dropdown
                    useDynamicFields={true}
                    required={true}
                    label="Active"
                    error={currentErrors.conActive}
                    module={MODULES.Master}
                    form={`${FORM}_${opeType}`}
                    field="con_active"
                    authenticated={true}
                    needDeletePermission={true}
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { conActive: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    placeholder="Active"
                    options={dropdown.activeOptions}
                    value={contactData.conActive}
                    style={{ height: "5px" }}
                    onChange={(e, data) => {
                      setContactData((prevState) => ({
                        ...prevState,
                        conActive: data.value,
                      }));
                    }}
                  /> */}
                  </div>
                </div>
              )}

              {isPhoneSectionVisible && (
                <div className="ui segment">
                  <div
                    className="ui dividing header"
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <h4>Phone Details</h4>
                    {secondPhoneField ? (
                      <div
                        style={{ marginBottom: 10 }}
                        className="ui red button"
                        onClick={() => {
                          setSecondPhoneField((prevState) => !prevState);
                        }}
                      >
                        <i style={{ margin: 0 }} className="minus icon"></i>
                      </div>
                    ) : (
                      <div
                        style={{ marginBottom: 10 }}
                        className="ui primary button"
                        onClick={() => {
                          setSecondPhoneField((prevState) => !prevState);
                        }}
                      >
                        <i style={{ margin: 0 }} className="plus icon"></i>
                      </div>
                    )}
                  </div>

                  <div className="two fields">
                    <Input
                      useDynamicFields={true}
                      label="Phone 1"
                      error={currentErrors.conPhone}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_phone"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conPhone: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="conPhone"
                      minLength={10}
                      value={contactData.conPhone}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conPhone: e.target.value,
                        }));
                      }}
                    />
                    <Input
                      useDynamicFields={true}
                      required={true}
                      label="Mobile "
                      error={currentErrors.conMobile}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_mobile"
                      editMode={editMode}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conMobile: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="conMobile"
                      minLength={10}
                      value={contactData.conMobile}
                      onChange={(e) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conMobile: e.target.value,
                        }));
                      }}
                    />
                  </div>

                  {secondPhoneField && (
                    <div className="two fields">
                      <Input
                        useDynamicFields={true}
                        label="Phone 2 "
                        error={currentErrors.conPhoneSecond}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_phone_second"
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conPhoneSecond: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        name="conPhone2"
                        minLength={10}
                        value={contactData.conPhoneSecond}
                        onChange={(e) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conPhoneSecond: e.target.value,
                          }));
                        }}
                      />
                      <Input
                        useDynamicFields={true}
                        required={true}
                        label="Whatsapp No "
                        error={currentErrors.conWhatsappNo}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_whatsapp_no"
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conWhatsappNo: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        name="conWhatsappNo"
                        minLength={10}
                        value={contactData.conWhatsappNo}
                        onChange={(e) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conWhatsappNo: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
              {isAddressSectionVisible && (
                <div className="ui segment">
                  <div
                    className="ui dividing header"
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <h4>Address Information</h4>
                    {secondAddressField ? (
                      <div
                        style={{ marginBottom: 10 }}
                        className="ui red button"
                        onClick={() => {
                          setSecondAddressField((prevState) => !prevState);
                        }}
                      >
                        <i style={{ margin: 0 }} className="minus icon"></i>
                      </div>
                    ) : (
                      <div
                        style={{ marginBottom: 10 }}
                        className="ui primary button"
                        onClick={() => {
                          setSecondAddressField((prevState) => !prevState);
                        }}
                      >
                        <i style={{ margin: 0 }} className="plus icon"></i>
                      </div>
                    )}
                  </div>

                  <div className="">
                    <div className="two fields">
                      <Input
                        useDynamicFields={true}
                        required={true}
                        label=" Address Line 1"
                        error={currentErrors.conAddress}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_address"
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conAddress: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        name="conAddress"
                        value={contactData.conAddress}
                        onChange={(e) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conAddress: e.target.value,
                          }));
                        }}
                      />
                      <Input
                        useDynamicFields={true}
                        label=" Address Line 2"
                        error={currentErrors.conAddress2}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_address2"
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conAddress2: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        name="conAddress2"
                        value={contactData.conAddress2}
                        onChange={(e) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conAddress2: e.target.value,
                          }));
                        }}
                      />
                    </div>
                    <SearchDropdown
                      useDynamicFields={true}
                      key={1}
                      ref={countryRef}
                      required={true}
                      defaultValue={
                        userCompany ? userCompany.comCountryName : ""
                      }
                      editMode={editMode}
                      module={MODULES.Master}
                      form={`${FORM}_${opeType}`}
                      field="con_country"
                      multiGrid={true}
                      multiGridCurrentValue={contactData.conCountryValue}
                      reset={searchFieldReset}
                      setReset={setSearchFieldReset}
                      clearable={true}
                      onClear={() => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conCountry: null,
                          conCountryValue: "",
                          conState: null,
                          conStateValue: "",
                          conDistrict: null,
                          conDistrictValue: "",
                          conCity: null,
                          conCityValue: "",
                        }));
                        setIsCountrySet(false);
                        setIsStateSet(false);
                        setIsDistrictSet(false);
                        countryRef.current.reset();
                        stateRef.current.reset();
                        districtRef.current.reset();
                        cityRef.current.reset();
                      }}
                      onResultSelect={(data) => {
                        setContactData((prevState) => ({
                          ...prevState,
                          conCountry: data,
                        }));
                        setIsCountrySet(true);
                      }}
                      api={countryStartsWith}
                      fieldInfo={{
                        tableName: "masterdata.tms_m_reg_country",
                        primaryKey: "cou_id",
                        fieldName: "cou_country",
                        id: contactData.conCountry,
                      }}
                      label="Country"
                      error={currentErrors.conCountry}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { conCountry: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                    />

                    <div className="two fields">
                      <SearchDropdown
                        useDynamicFields={true}
                        key={1}
                        ref={stateRef}
                        required={true}
                        editMode={editMode}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_state"
                        multiGrid={true}
                        multiGridCurrentValue={contactData.conStateValue}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        clearable={true}
                        onClear={() => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conState: null,
                            conStateValue: "",
                            conDistrict: null,
                            conDistrictValue: "",
                            conCity: null,
                            conCityValue: "",
                          }));
                          setIsStateSet(false);
                          setIsDistrictSet(false);
                          stateRef.current.reset();
                          districtRef.current.reset();
                          cityRef.current.reset();
                        }}
                        onResultSelect={(data) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conState: data,
                            conDistrict: null,
                            conDistrictValue: "",
                            conCity: null,
                            conCityValue: "",
                          }));
                          setIsStateSet(true);
                          setIsDistrictSet(false);
                          districtRef.current.reset();
                          cityRef.current.reset();
                        }}
                        api={stateByCountryAndStartsWith}
                        fieldInfo={{
                          tableName: "masterdata.tms_m_reg_state",
                          primaryKey: "stt_id",
                          fieldName: "stt_state_name",
                          id: contactData.conState,
                        }}
                        apiDependency={{
                          id: contactData.conCountry,
                        }}
                        label="State"
                        error={currentErrors.conState}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conState: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        disabled={!isCountrySet}
                      />

                      <SearchDropdown
                        useDynamicFields={true}
                        ref={districtRef}
                        key={1}
                        required={true}
                        editMode={editMode}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_district"
                        reset={searchFieldReset}
                        multiGrid={true}
                        multiGridCurrentValue={contactData.conDistrictValue}
                        setReset={setSearchFieldReset}
                        clearable={true}
                        onClear={() => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conDistrict: null,
                            conDistrictValue: "",
                            conCity: null,
                            conCityValue: "",
                          }));
                          setIsDistrictSet(false);
                          districtRef.current.reset();
                          cityRef.current.reset();
                        }}
                        onResultSelect={(data) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conDistrict: data,
                          }));
                          setIsDistrictSet(true);
                        }}
                        api={districtByStateAndStartsWith}
                        apiDependency={{ id: contactData.conState }}
                        fieldInfo={{
                          tableName: "masterdata.tms_m_reg_district",
                          primaryKey: "dis_id",
                          fieldName: "dis_district",
                          id: contactData.conDistrict,
                        }}
                        label="District"
                        error={currentErrors.conDistrict}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conDistrict: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        disabled={!isStateSet}
                      />
                    </div>
                    <div className="two fields">
                      <SearchDropdown
                        useDynamicFields={true}
                        key={1}
                        ref={cityRef}
                        required={true}
                        editMode={editMode}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_city"
                        reset={searchFieldReset}
                        multiGrid={true}
                        multiGridCurrentValue={contactData.conCityValue}
                        setReset={setSearchFieldReset}
                        clearable={true}
                        onClear={() => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conCity: null,
                          }));
                        }}
                        onResultSelect={(data) =>
                          setContactData((prevState) => ({
                            ...prevState,
                            conCity: data,
                          }))
                        }
                        api={
                          isDistrictSet
                            ? cityByDistrictAndStartsWith
                            : isStateSet
                              ? cityByStateAndStartsWith
                              : isCountrySet
                                ? cityByCountryAndStartsWith
                                : cityStartsWith
                        }
                        apiDependency={
                          isDistrictSet
                            ? { id: contactData.conDistrict }
                            : isStateSet
                              ? { id: contactData.conState }
                              : isCountrySet
                                ? { id: contactData.conCountry }
                                : {}
                        }
                        fieldInfo={{
                          tableName: "masterdata.tms_m_reg_cities",
                          primaryKey: "cit_id",
                          fieldName: "cit_city",
                          id: contactData.conCity,
                        }}
                        label="City"
                        error={currentErrors.conCity}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conCity: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />
                      <Input
                        useDynamicFields={true}
                        label="Zip"
                        error={currentErrors.conZip}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_zip"
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conZip: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        name="conZip"
                        value={contactData.conZip}
                        onChange={(e) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conZip: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {secondAddressField && (
                    <>
                      <div className="ui divider"></div>
                      <div className="two fields">
                        <Input
                          useDynamicFields={true}
                          label="Address Line 1"
                          error={currentErrors.conSecAddress}
                          module={MODULES.Master}
                          form={`${FORM}_${opeType}`}
                          field="con_sec_address"
                          editMode={editMode}
                          resetError={() =>
                            setCurrentErrors((prevState) => {
                              const { conSecAddress: _, ...rest } = prevState;
                              return rest;
                            })
                          }
                          name="conSecAddress"
                          value={contactData.conSecAddress}
                          onChange={(e) => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecAddress: e.target.value,
                            }));
                          }}
                        />
                        <Input
                          useDynamicFields={true}
                          label="Address Line 2"
                          error={currentErrors.conSecAddress2}
                          module={MODULES.Master}
                          form={`${FORM}_${opeType}`}
                          field="con_sec_address2"
                          editMode={editMode}
                          resetError={() =>
                            setCurrentErrors((prevState) => {
                              const { conSecAddress2: _, ...rest } = prevState;
                              return rest;
                            })
                          }
                          name="conSecAddress2"
                          value={contactData.conSecAddress2}
                          onChange={(e) => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecAddress2: e.target.value,
                            }));
                          }}
                        />
                      </div>
                      <SearchDropdown
                        useDynamicFields={true}
                        key={1}
                        ref={secCountryRef}
                        defaultValue={
                          userCompany ? userCompany.comCountryName : ""
                        }
                        editMode={editMode}
                        module={MODULES.Master}
                        form={`${FORM}_${opeType}`}
                        field="con_sec_country"
                        reset={searchFieldReset}
                        multiGrid={true}
                        multiGridCurrentValue={contactData.conSecCountryValue}
                        setReset={setSearchFieldReset}
                        clearable={true}
                        onClear={() => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conSecCountry: null,
                            conSecCountryValue: "",
                            conSecState: null,
                            conSecStateValue: "",
                            conSecDistrict: null,
                            conSecDistrictValue: "",
                            conSecCity: null,
                            conSecCityValue: "",
                          }));
                          setIsSecCountrySet(false);
                          setIsSecStateSet(false);
                          setIsSecDistrictSet(false);
                          secCountryRef.current.reset();
                          secStateRef.current.reset();
                          secDistrictRef.current.reset();
                          secCityRef.current.reset();
                        }}
                        onResultSelect={(data) => {
                          setContactData((prevState) => ({
                            ...prevState,
                            conSecCountry: data,
                          }));
                          setIsSecCountrySet(true);
                        }}
                        api={countryStartsWith}
                        fieldInfo={{
                          tableName: "masterdata.tms_m_reg_country",
                          primaryKey: "cou_id",
                          fieldName: "cou_country",
                          id: contactData.conSecCountry,
                        }}
                        label="Country"
                        error={currentErrors.conSecCountry}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { conSecCountry: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />

                      <div className="two fields">
                        <SearchDropdown
                          useDynamicFields={true}
                          key={1}
                          ref={secStateRef}
                          editMode={editMode}
                          module={MODULES.Master}
                          form={`${FORM}_${opeType}`}
                          field="con_sec_state"
                          reset={searchFieldReset}
                          setReset={setSearchFieldReset}
                          multiGrid={true}
                          multiGridCurrentValue={contactData.conSecStateValue}
                          clearable={true}
                          onClear={() => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecState: null,
                              conSecStateValue: "",
                              conSecDistrict: null,
                              conSecDistrictValue: "",
                              conSecCity: null,
                              conSecCityValue: "",
                            }));
                            setIsSecStateSet(false);
                            setIsSecDistrictSet(false);
                            secStateRef.current.reset();
                            secDistrictRef.current.reset();
                            secCityRef.current.reset();
                          }}
                          onResultSelect={(data) => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecState: data,
                              conSecDistrict: null,
                              conSecDistrictValue: "",
                              conSecCity: null,
                              conSecCityValue: "",
                            }));
                            setIsSecStateSet(true);
                            setIsSecDistrictSet(false);
                            secDistrictRef.current.reset();
                            secCityRef.current.reset();
                          }}
                          api={stateByCountryAndStartsWith}
                          fieldInfo={{
                            tableName: "masterdata.tms_m_reg_state",
                            primaryKey: "stt_id",
                            fieldName: "stt_state_name",
                            id: contactData.conSecState,
                          }}
                          apiDependency={{
                            id: contactData.conSecCountry,
                          }}
                          label="State"
                          error={currentErrors.conSecState}
                          resetError={() =>
                            setCurrentErrors((prevState) => {
                              const { conSecState: _, ...rest } = prevState;
                              return rest;
                            })
                          }
                          disabled={!isSecCountrySet}
                        />
                        <SearchDropdown
                          useDynamicFields={true}
                          ref={secDistrictRef}
                          key={1}
                          editMode={editMode}
                          module={MODULES.Master}
                          form={`${FORM}_${opeType}`}
                          field="con_sec_district"
                          reset={searchFieldReset}
                          setReset={setSearchFieldReset}
                          multiGrid={true}
                          multiGridCurrentValue={
                            contactData.conSecDistrictValue
                          }
                          clearable={true}
                          onClear={() => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecDistrict: null,
                              conSecDistrictValue: "",
                              conSecCity: null,
                              conSecCityValue: "",
                            }));
                            setIsSecDistrictSet(false);
                            secDistrictRef.current.reset();
                            secCityRef.current.reset();
                          }}
                          onResultSelect={(data) => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecDistrict: data,
                            }));
                            setIsSecDistrictSet(true);
                          }}
                          api={districtByStateAndStartsWith}
                          apiDependency={{ id: contactData.conSecState }}
                          fieldInfo={{
                            tableName: "masterdata.tms_m_reg_district",
                            primaryKey: "dis_id",
                            fieldName: "dis_district",
                            id: contactData.conSecDistrict,
                          }}
                          label="District"
                          error={currentErrors.conSecDistrict}
                          resetError={() =>
                            setCurrentErrors((prevState) => {
                              const { conSecDistrict: _, ...rest } = prevState;
                              return rest;
                            })
                          }
                          disabled={!isSecStateSet}
                        />
                      </div>
                      <div className="two fields">
                        <SearchDropdown
                          useDynamicFields={true}
                          key={1}
                          ref={secCityRef}
                          editMode={editMode}
                          module={MODULES.Master}
                          form={`${FORM}_${opeType}`}
                          field="con_sec_city"
                          reset={searchFieldReset}
                          setReset={setSearchFieldReset}
                          multiGrid={true}
                          multiGridCurrentValue={contactData.conSecCityValue}
                          clearable={true}
                          onClear={() => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecCity: null,
                            }));
                          }}
                          onResultSelect={(data) =>
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecCity: data,
                            }))
                          }
                          api={
                            isSecDistrictSet
                              ? cityByDistrictAndStartsWith
                              : isSecStateSet
                                ? cityByStateAndStartsWith
                                : isSecCountrySet
                                  ? cityByCountryAndStartsWith
                                  : cityStartsWith
                          }
                          apiDependency={
                            isSecDistrictSet
                              ? { id: contactData.conSecDistrict }
                              : isSecStateSet
                                ? { id: contactData.conSecState }
                                : isSecCountrySet
                                  ? { id: contactData.conSecCountry }
                                  : {}
                          }
                          fieldInfo={{
                            tableName: "masterdata.tms_m_reg_cities",
                            primaryKey: "cit_id",
                            fieldName: "cit_city",
                            id: contactData.conSecCity,
                          }}
                          label="City"
                          error={currentErrors.conSecCity}
                          resetError={() =>
                            setCurrentErrors((prevState) => {
                              const { conSecCity: _, ...rest } = prevState;
                              return rest;
                            })
                          }
                        />
                        <Input
                          useDynamicFields={true}
                          label="Zip"
                          error={currentErrors.conSecZip}
                          module={MODULES.Master}
                          form={`${FORM}_${opeType}`}
                          field="con_sec_zip"
                          editMode={editMode}
                          resetError={() =>
                            setCurrentErrors((prevState) => {
                              const { conSecZip: _, ...rest } = prevState;
                              return rest;
                            })
                          }
                          name="conSecZip"
                          value={contactData.conSecZip}
                          onChange={(e) => {
                            setContactData((prevState) => ({
                              ...prevState,
                              conSecZip: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}_${opeType}`}
                  create={Object.keys(currentContact).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <small style={{ color: "#9f3a38" }}>{`${
                currentErrors.mainCountError ? currentErrors.mainCountError : ""
              }`}</small>
              <Grid
                heading="Contact List"
                config={
                  opeType === 5 || opeType === 9
                    ? [
                        { headerName: "Address", field: "conAddress" },
                        { headerName: "State", field: "conStateValue" },
                        { headerName: "City", field: "conCityValue" },
                        {
                          field: "Edit",
                          filter: true,
                          cellRenderer: ButtonRenderer,
                          cellRendererParams: (params) => ({
                            clickHandler: () => {
                              setCurrentContact(params.data);
                            },
                            label: "Edit",
                            authenticated: true,
                            form: `${FORM}_${opeType}`,
                            module: MODULES.Master,
                            editMode: editMode,
                            consider: AUTH_TYPES.CREATE,
                          }),
                        },
                        {
                          field: "Delete",
                          filter: true,
                          cellRenderer: DeleteButtonRenderer,
                          cellRendererParams: (params) => ({
                            consider: AUTH_TYPES.DELETE,
                            clickHandler: () => {
                              if (
                                params.data.conId &&
                                params.data.conId !== null
                              ) {
                                setDeletedList((prevState) => [
                                  ...prevState,
                                  params.data.conId,
                                ]);
                              }
                              setContacts((prevContacts) =>
                                prevContacts.filter(
                                  (contact) => contact.id !== params.data.id,
                                ),
                              );
                            },
                             message:
                              "Are you sure you want to delete this Contact Details?",
                            label: "Delete",
                            className: "ui red button",
                            authenticated: true,
                            form: `${FORM}_${opeType}`,
                            module: MODULES.Master,
                            editMode:
                              params.data.conId !== null &&
                              isNumber(params.data.conId),
                          }),
                        },
                      ]
                    : [
                        { headerName: "Type", field: "conTypeValue" },
                        { headerName: "Title", field: "conTitleValue" },
                        {
                          headerName: "Name",
                          field: "conName",
                          cellRenderer: ResizableScrollingTextRenderer,
                        },
                        { headerName: "Phone", field: "conPhone" },
                        { headerName: "Mobile", field: "conMobile" },
                        {
                          headerName: "Email",
                          field: "conEmail",
                          cellRenderer: ResizableScrollingTextRenderer,
                        },
                        {
                          field: "Edit",
                          filter: true,
                          cellRenderer: ButtonRenderer,
                          cellRendererParams: (params) => ({
                            clickHandler: () => {
                              setCurrentContact(params.data);
                            },
                            label: "Edit",
                            authenticated: true,
                            form: `${FORM}_${opeType}`,
                            module: MODULES.Master,
                            editMode: editMode,
                            consider: AUTH_TYPES.CREATE,
                          }),
                        },
                        {
                          field: "Delete",
                          filter: true,
                          cellRenderer: DeleteButtonRenderer,
                          cellRendererParams: (params) => ({
                            consider: AUTH_TYPES.DELETE,
                            clickHandler: () => {
                              if (
                                params.data.conId &&
                                params.data.conId !== null
                              ) {
                                setDeletedList((prevState) => [
                                  ...prevState,
                                  params.data.conId,
                                ]);
                              }
                              setContacts((prevContacts) =>
                                prevContacts.filter(
                                  (contact) => contact.id !== params.data.id,
                                ),
                              );
                            },
                            message:
                              "Are you sure you want to delete this Contact Details?",
                            label: "Delete",
                            className: "ui red button",
                            authenticated: true,
                            form: `${FORM}_${opeType}`,
                            module: MODULES.Master,
                            editMode:
                              params.data.conId !== null &&
                              isNumber(params.data.conId),
                          }),
                        },
                      ]
                }
                content={contacts}
              />
            </form>
          </>
        )}
      </>
    );
  },
);

export default ContactComponent;
